import { useState, useEffect, useMemo } from "react"
import { useParams, useNavigate } from "react-router-dom"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Skeleton } from "@/components/ui/skeleton"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { Progress } from "@/components/ui/progress"
import { Spinner } from "@/components/ui/spinner"
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table"
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip"
import { 
  ArrowLeft, 
  RefreshCw, 
  Calendar, 
  Users, 
  FolderOpen, 
  DollarSign,
  CheckCircle,
  Clock,
  AlertCircle,
  ExternalLink,
  ChevronLeft,
  ChevronRight,
  TrendingUp,
  Star,
  AlertTriangle,
  Filter,
  Search
} from "lucide-react"
import { ChartContainer, ChartTooltip, ChartTooltipContent } from "@/components/ui/chart"
// @ts-ignore - recharts types are outdated
import { BarChart, Bar, XAxis, YAxis, Cell, PieChart, Pie } from 'recharts'
import { fetchProductionManagement } from "@/lib/notion-client"
import { useSprints } from "@/lib/sprint-context"
import { useProductionManagement } from "@/lib/production-management-context"
import { NotionAvatar } from "@/components/notion-avatar"
import { NotionIcon } from "@/components/icons/notion-icon"
import { getStatusColors } from "@/lib/status-colors"
import { getTagColors } from "@/lib/tag-colors"
import type { SprintTask } from "@/lib/types"
// import { toast } from "@/hooks/use-toast"

interface Project {
  id: string
  name: string
  status?: string
  startDate?: string
  endDate?: string
  taskCount?: string | number
  budget?: string | number
  budgetRealised?: string | number | null
  projectCode?: string
  priority?: string
  gdriveRoom?: string
  description?: string
  team?: string[]
  progress?: number
  [key: string]: any
}

interface TaskStats {
  total: number
  completed: number
  inProgress: number
  todo: number
  overdue: number
}

export default function ProductionManagementDetailPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { sprintTasks: allSprintTasks, sprints, loadingSprints, refreshSprints } = useSprints()
  const { resources, getUsersByIds, getUsersByEmails, calculateRealizedBudget } = useProductionManagement()
  const [project, setProject] = useState<Project | null>(null)
  const [currentSprintOffset, setCurrentSprintOffset] = useState(0)
  const [loading, setLoading] = useState(true)
  const [refreshing, setRefreshing] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [taskSearch, setTaskSearch] = useState("")
  const [selectedTags, setSelectedTags] = useState<string[]>([])
  const [selectedStatuses, setSelectedStatuses] = useState<string[]>([])
  const [activeTab, setActiveTab] = useState("overview")

  // Helper function to get assignee names from emails
  const getAssigneeNames = (assigneeEmails?: string[]): string => {
    const wpConfig = (window as any).WP_COCKPIT
    const isDev = wpConfig?.devMode || false
    
    if (isDev) {
      console.log('[ProductionDetailPage] getAssigneeNames called')
      console.log('[ProductionDetailPage] assigneeEmails:', assigneeEmails)
    }
    
    if (!assigneeEmails || assigneeEmails.length === 0) {
      if (isDev) console.log('[ProductionDetailPage] No assignee emails provided')
      return ""
    }
    
    const users = getUsersByEmails(assigneeEmails)
    
    if (isDev) {
      console.log('[ProductionDetailPage] Matched users:', users)
      console.log('[ProductionDetailPage] Number of matches:', users.length)
    }
    
    if (users.length === 0) {
      if (isDev) console.log('[ProductionDetailPage] No users matched, showing count')
      return `${assigneeEmails.length} assignee${assigneeEmails.length !== 1 ? "s" : ""}`
    }
    
    const names = users.map(u => u.name).join(", ")
    if (isDev) console.log('[ProductionDetailPage] Returning names:', names)
    return names
  }

  // Helper function to get contributor names from IDs
  const getContributorNames = (contributorIds?: string[]): string => {
    const wpConfig = (window as any).WP_COCKPIT
    const isDev = wpConfig?.devMode || false
    
    if (isDev && contributorIds && contributorIds.length > 0) {
      console.log('[ProductionDetailPage] getContributorNames called with:', contributorIds)
      console.log('[ProductionDetailPage] Available resources:', resources.length)
      if (resources.length > 0) {
        console.log('[ProductionDetailPage] First resource ID:', resources[0].id, 'Name:', resources[0].name)
        console.log('[ProductionDetailPage] All resource IDs:', resources.map(r => r.id).slice(0, 5))
      }
    }
    
    if (!contributorIds || contributorIds.length === 0) return ''
    
    const names = contributorIds
      .map(id => {
        const resource = resources.find(r => r.id === id)
        if (isDev) {
          console.log('[ProductionDetailPage] Looking for resource with id:', id, 'Found:', resource?.name || 'NOT FOUND')
        }
        return resource?.name?.trim() || null
      })
      .filter(Boolean) as string[]
    
    const result = names.length > 0 ? names.join(', ') : ''
    
    if (isDev) {
      console.log('[ProductionDetailPage] Matched names:', names)
      console.log('[ProductionDetailPage] Final result:', result, 'Length:', result.length)
    }
    
    return result
  }

  // Filter sprint tasks by this project ID
  const sprintTasks = useMemo(() => {
    return allSprintTasks.filter(task => task.projectId === id)
  }, [allSprintTasks, id])

  // Calculate realized budget from timesheets
  const realizedBudgetData = useMemo(() => {
    if (sprintTasks.length === 0) {
      return {
        totalRealized: 0,
        totalHours: 0,
        byTask: [],
        byResource: []
      }
    }
    
    // Create task title map for better display
    const taskTitleMap = new Map<string, string>()
    sprintTasks.forEach(task => {
      taskTitleMap.set(task.id, task.title || 'Untitled Task')
    })
    
    const taskIds = sprintTasks.map(task => task.id)
    return calculateRealizedBudget(taskIds, taskTitleMap)
  }, [sprintTasks, calculateRealizedBudget])

  const availableTags = useMemo(() => {
    const tagSet = new Set<string>()
    sprintTasks.forEach(task => {
      task.tags?.forEach(tag => tagSet.add(tag))
    })
    return Array.from(tagSet).sort((a, b) => a.localeCompare(b))
  }, [sprintTasks])

  const statusOptions = useMemo(() => {
    const statuses = new Set<string>()
    sprintTasks.forEach(task => {
      if (task.status) {
        statuses.add(task.status)
      }
    })
    return Array.from(statuses).sort((a, b) => a.localeCompare(b))
  }, [sprintTasks])

  const filteredTasks = useMemo(() => {
    const searchTerm = taskSearch.trim().toLowerCase()
    const normalizedStatuses = selectedStatuses.map(status => status.toLowerCase())

    // Status priority order: Planned first, then In Progress, Quality Check, Backlog, and Done/Archived last
    const getStatusPriority = (status: string): number => {
      const s = status.toLowerCase();
      if (s.includes('planned')) return 0;
      if (s.includes('in progress') || s.includes('in-progress')) return 1;
      if (s.includes('quality') || s.includes('review')) return 2;
      if (s.includes('backlog')) return 3;
      if (s.includes('done') || s.includes('completed') || s.includes('archived')) return 5;
      return 4; // Other statuses
    };

    const filtered = sprintTasks.filter(task => {
      const taskStatus = (task.status || "").toLowerCase()

      if (normalizedStatuses.length > 0 && !normalizedStatuses.includes(taskStatus)) {
        return false
      }

      if (selectedTags.length > 0) {
        const taskTags = task.tags || []
        const matchesTag = taskTags.some(tag => selectedTags.includes(tag))
        if (!matchesTag) {
          return false
        }
      }

      if (searchTerm) {
        const searchableText = [
          task.title,
          task.description,
          task.status,
          task.priority,
          ...(task.tags || []),
          ...(task.assignee || []),
        ]
          .filter(Boolean)
          .join(" ")
          .toLowerCase()

        if (!searchableText.includes(searchTerm)) {
          return false
        }
      }

      return true
    });

    // Sort by status priority
    return filtered.sort((a, b) => {
      const priorityA = getStatusPriority(a.status || '');
      const priorityB = getStatusPriority(b.status || '');
      return priorityA - priorityB;
    });
  }, [sprintTasks, taskSearch, selectedTags, selectedStatuses])

  const hasActiveTaskFilters =
    selectedStatuses.length > 0 || selectedTags.length > 0 || taskSearch.trim().length > 0

  const toggleTag = (tag: string) => {
    setSelectedTags(prev =>
      prev.includes(tag) ? prev.filter(existingTag => existingTag !== tag) : [...prev, tag]
    )
  }

  const toggleStatus = (status: string) => {
    setSelectedStatuses(prev =>
      prev.includes(status)
        ? prev.filter(existingStatus => existingStatus !== status)
        : [...prev, status]
    )
  }

  const resetTaskFilters = () => {
    setTaskSearch("")
    setSelectedTags([])
    setSelectedStatuses([])
  }

  const renderedTaskCards = filteredTasks.map((task) => {
    const wpConfig = (window as any).WP_COCKPIT
    const isDev = wpConfig?.devMode || false
    if (isDev && task.contributors) {
      console.log('[TasksTab] Rendering task:', task.title, 'Contributors:', task.contributors, 'Length:', task.contributors.length)
    }

    const isOverdue = task.due && task.due < new Date() && 
      !task.status?.toLowerCase().includes("done") && 
      !task.status?.toLowerCase().includes("completed")

    return (
      <Card 
        key={task.id} 
        className="border-l-4 cursor-pointer hover:shadow-md transition-shadow"
        style={{ borderLeftColor: isOverdue ? '#ef4444' : getStatusColors(task.status).borderColorValue }}
        onClick={() => navigate(`/production-management/task/${task.id}`, { state: { task } })}
      >
        <CardHeader>
          <div className="flex items-start justify-between gap-4">
            <div className="flex-1">
              <CardTitle className="text-base">{task.title}</CardTitle>
              {task.description && (
                <CardDescription className="mt-1 line-clamp-2">
                  {task.description}
                </CardDescription>
              )}
            </div>
            <Badge 
              className={`${getStatusColors(task.status || "").bg} ${getStatusColors(task.status || "").text} ${getStatusColors(task.status || "").border} border`}
            >
              {task.status || "Not Started"}
            </Badge>
          </div>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-4 text-sm text-muted-foreground">
            {task.due && (
              <div className={`flex items-center gap-1 ${isOverdue ? "text-red-500 font-medium" : ""}`}>
                <Calendar className="h-3 w-3" />
                {task.due.toLocaleDateString()}
                {isOverdue && " (Overdue)"}
              </div>
            )}
            {(task.estimatedHours !== undefined || task.realisedHours !== undefined) && (
              <div className="flex items-center gap-2">
                <Clock className="h-3 w-3" />
                {task.realisedHours !== undefined && task.estimatedHours !== undefined ? (
                  <>
                    <span className={`${
                      task.realisedHours > task.estimatedHours ? 'text-red-600 font-semibold' :
                      task.realisedHours >= task.estimatedHours * 0.8 ? 'text-orange-600 font-medium' :
                      'text-green-600'
                    }`}>
                      {task.realisedHours.toFixed(1)}h / {task.estimatedHours}h
                    </span>
                    <div className="flex-1 min-w-[60px] h-2 bg-gray-200 rounded-full overflow-hidden">
                      <div 
                        className={`h-full transition-all ${
                          task.realisedHours > task.estimatedHours ? 'bg-red-500' :
                          task.realisedHours >= task.estimatedHours * 0.8 ? 'bg-orange-500' :
                          'bg-green-500'
                        }`}
                        style={{ width: `${Math.min(100, (task.realisedHours / task.estimatedHours) * 100)}%` }}
                      />
                    </div>
                  </>
                ) : task.realisedHours !== undefined ? (
                  <span>{task.realisedHours.toFixed(1)}h</span>
                ) : (
                  <span>{task.estimatedHours}h</span>
                )}
              </div>
            )}
            {task.assignee && task.assignee.length > 0 && (
              <div className="flex items-center gap-1">
                <Users className="h-3 w-3" />
                <span className="font-medium">{getAssigneeNames(task.assignee)}</span>
              </div>
            )}
            {task.contributors && task.contributors.length > 0 && (
              <div className="flex items-center gap-1">
                <Users className="h-3 w-3" />
                {(() => {
                  const names = getContributorNames(task.contributors)
                  const result = names ? names : `${task.contributors.length} contributor${task.contributors.length !== 1 ? "s" : ""}`
                  if (isDev) {
                    console.log('[TasksTab RENDER] Task:', task.title, 'Names result:', names, 'Is truthy?', !!names, 'Final display:', result)
                  }
                  return result
                })()}
              </div>
            )}
            {task.url && (
              <a
                href={task.url}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-1 text-blue-500 hover:underline"
                onClick={(e) => e.stopPropagation()}
              >
                <ExternalLink className="h-3 w-3" />
                View in Notion
              </a>
            )}
          </div>
          {task.tags && task.tags.length > 0 && (
            <div className="flex flex-wrap gap-1 mt-3">
              {task.tags.map((tag, idx) => (
                <Badge key={idx} className={`${getTagColors(tag).bg} ${getTagColors(tag).text} ${getTagColors(tag).border} border text-xs`}>
                  {tag}
                </Badge>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    )
  })

  const fetchProjectData = async (force: boolean = false) => {
    if (!id) return

    try {
      if (force) setRefreshing(true)
      else setLoading(true)

      const data = await fetchProductionManagement(force)
      
      if (data?.projects) {
        const foundProject = data.projects.find((p: Project) => p.id === id)
        if (foundProject) {
          setProject(foundProject)
        } else {
          setError("Project not found")
        }
      } else {
        setError("No projects data available")
      }
    } catch (err) {
      console.error("Error fetching project:", err)
      setError("Failed to load project details")
    } finally {
      setLoading(false)
      setRefreshing(false)
    }
  }

  // Tasks are now filtered from sprintTasks context - no separate fetch needed

  // Sprint data is now handled by SprintContext
  
  const refreshSprintData = async (force: boolean = false) => {
    try {
      if (force) setRefreshing(true)
      await refreshSprints(force)
    } catch (err) {
      console.error("Error refreshing sprint data:", err)
    } finally {
      if (force) setRefreshing(false)
    }
  }

  useEffect(() => {
    fetchProjectData()
  }, [id])

  // Initialize sprint offset to current sprint
  useEffect(() => {
    if (sprints.length > 0 && currentSprintOffset === 0) {
      const currentSprintIndex = sprints.findIndex(s => 
        s.status?.toLowerCase() === 'current' || 
        s.status?.toLowerCase() === 'active' || 
        s.status?.toLowerCase() === 'in progress'
      )
      if (currentSprintIndex !== -1) {
        setCurrentSprintOffset(currentSprintIndex)
      }
    }
  }, [sprints])

  const handleRefresh = () => {
    fetchProjectData(true)
    refreshSprintData(true)
  }

  const getPriorityColor = (priority?: string) => {
    if (!priority) return "text-gray-500"
    const lowerPriority = priority.toLowerCase()
    if (lowerPriority.includes("high")) return "text-red-500"
    if (lowerPriority.includes("medium")) return "text-yellow-500"
    if (lowerPriority.includes("low")) return "text-green-500"
    return "text-gray-500"
  }

  const formatBudget = (budget?: string | number) => {
    if (!budget) return null
    const amount = typeof budget === 'string' ? parseFloat(budget) : budget
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount)
  }

  const computeTaskStats = (tasks: SprintTask[]): TaskStats => {
    const stats: TaskStats = {
      total: tasks.length,
      completed: 0,
      inProgress: 0,
      todo: 0,
      overdue: 0,
    }

    const now = new Date()

    tasks.forEach(task => {
      const status = task.status?.toLowerCase() || ""
      
      if (status.includes("done") || status.includes("completed") || status.includes("archived")) {
        stats.completed++
      } else if (status.includes("progress") || status.includes("quality") || status.includes("maintenance")) {
        stats.inProgress++
      } else {
        stats.todo++
      }

      if (task.due && task.due < now && !status.includes("done") && !status.includes("completed")) {
        stats.overdue++
      }
    })

    return stats
  }

  const overallTaskStats = useMemo(() => computeTaskStats(sprintTasks), [sprintTasks])
  const filteredTaskStats = useMemo(() => computeTaskStats(filteredTasks), [filteredTasks])
  const completionRate = overallTaskStats.total > 0 
    ? Math.round((overallTaskStats.completed / overallTaskStats.total) * 100) 
    : 0

  // Calculate project-specific KPIs
  const projectKPIs = useMemo(() => {
    // Get contributors from sprint tasks
    const contributorsSet = new Set<string>()
    sprintTasks.forEach(task => {
      if (Array.isArray(task.contributors)) {
        task.contributors.forEach(c => contributorsSet.add(c))
      }
    })
    const totalContributors = contributorsSet.size

    // Calculate estimated hours capacity from sprint tasks
    const totalEstimatedHours = sprintTasks.reduce((sum, task) => sum + (task.estimatedHours || 0), 0)

    // NEW: Calculate utilization per tag (exclude Done/Archived tasks)
    // Group tasks by tags and calculate remaining hours
    const tagUtilization: Record<string, { totalCapacity: number; remainingHours: number }> = {}
    
    sprintTasks.forEach(task => {
      const taskStatus = task.status?.toLowerCase() || '';
      // Skip Done/Archived/Completed tasks
      if (taskStatus.includes('done') || taskStatus.includes('archived') || taskStatus.includes('completed')) {
        return;
      }
      
      const tags = task.tags || []
      const estimatedHours = task.estimatedHours || 0
      const realisedHours = task.realisedHours || 0
      const remainingHours = Math.max(0, estimatedHours - realisedHours)
      
      tags.forEach(tag => {
        if (!tagUtilization[tag]) {
          tagUtilization[tag] = { totalCapacity: 0, remainingHours: 0 }
        }
        tagUtilization[tag].remainingHours += remainingHours
      })
    })

    // Calculate total capacity per tag based on contributors (exclude Done/Archived tasks)
    // For now, we'll distribute the total capacity across tags
    // In a more sophisticated system, you'd track capacity per tag per resource
    const totalTagCapacity = Object.keys(tagUtilization).reduce((sum, tag) => {
      // Get all resources that work on this tag
      const contributorsForTag = new Set<string>()
      sprintTasks.forEach(task => {
        const taskStatus = task.status?.toLowerCase() || '';
        if (taskStatus.includes('done') || taskStatus.includes('archived') || taskStatus.includes('completed')) {
          return;
        }
        if (task.tags?.includes(tag) && task.contributors) {
          task.contributors.forEach(c => contributorsForTag.add(c))
        }
      })
      
      // Calculate capacity for this tag (weekly capacity / number of unique tags they work on)
      let tagCapacity = 0
      contributorsForTag.forEach(contributorId => {
        const resource = resources.find(r => r.id === contributorId)
        if (resource) {
          const weeklyCapacity = typeof resource.weeklyCapacity === 'string' 
            ? parseFloat(resource.weeklyCapacity) 
            : (resource.weeklyCapacity || 0)
          // Simple distribution: divide by number of tags the resource works on
          const resourceTags = new Set<string>()
          sprintTasks.forEach(task => {
            const taskStatus = task.status?.toLowerCase() || '';
            if (taskStatus.includes('done') || taskStatus.includes('archived') || taskStatus.includes('completed')) {
              return;
            }
            if (task.contributors?.includes(contributorId) && task.tags) {
              task.tags.forEach(t => resourceTags.add(t))
            }
          })
          tagCapacity += weeklyCapacity / Math.max(1, resourceTags.size)
        }
      })
      
      tagUtilization[tag].totalCapacity = tagCapacity
      return sum + tagCapacity
    }, 0)

    // Calculate overall utilization: (total remaining hours) / (total capacity)
    const totalRemainingHours = Object.values(tagUtilization).reduce((sum, tag) => sum + tag.remainingHours, 0)
    const utilization = totalTagCapacity > 0 
      ? Math.round((totalRemainingHours / totalTagCapacity) * 100)
      : 0

    // Budget Health: percentage of budget remaining (using calculated realized budget from timesheets)
    const budget = typeof project?.budget === 'string' ? parseFloat(project.budget) : (project?.budget || 0)
    const spent = realizedBudgetData.totalRealized
    const budgetHealth = budget > 0 ? Math.round(((budget - spent) / budget) * 100) : 100

    // Quality Score: project progress
    const qualityScore = project?.progress || 0

    // Timeline Risk: check if project is past due or tasks are overdue
    const now = new Date()
    let timelineRisk = 0
    
    if (project?.endDate) {
      const endDate = new Date(project.endDate)
      const progress = project.progress || 0
      if (endDate < now && progress < 100) {
        timelineRisk = 100 // Critical risk if past end date
      }
    }
    
    // Also check for overdue tasks
    const overdueTasks = sprintTasks.filter(task => {
      if (!task.due) return false
      const status = task.status?.toLowerCase() || ''
      return task.due < now && !status.includes('done') && !status.includes('completed')
    }).length
    
    if (overdueTasks > 0 && sprintTasks.length > 0) {
      const taskRisk = Math.round((overdueTasks / sprintTasks.length) * 100)
      timelineRisk = Math.max(timelineRisk, taskRisk)
    }

    return {
      capacity: totalEstimatedHours,
      contributors: totalContributors,
      utilization,
      budgetHealth,
      qualityScore,
      timelineRisk
    }
  }, [project, sprintTasks, resources, realizedBudgetData])

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Skeleton className="h-10 w-48" />
        <div className="grid gap-6 md:grid-cols-3">
          <Skeleton className="h-32" />
          <Skeleton className="h-32" />
          <Skeleton className="h-32" />
        </div>
        <Skeleton className="h-96" />
        </div>
      </div>
    )
  }

  if (error || !project) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Button 
          onClick={() => navigate("/production-management")} 
          variant="outline" 
          className="mb-4"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Projects
        </Button>
        <Card>
          <CardContent className="py-12 text-center">
            <p className="text-muted-foreground">{error || "Project not found"}</p>
          </CardContent>
        </Card>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <div className="mb-8">
        <Button 
          onClick={() => navigate("/production-management")} 
          variant="outline" 
          className="mb-4"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Projects
        </Button>
        
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <div className="flex items-center gap-3 mb-2">
              <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100">{project.name}</h1>
              <Badge 
                className={`${getStatusColors(project.status || "").bg} ${getStatusColors(project.status || "").text} ${getStatusColors(project.status || "").border} border`}
              >
                {project.status || "Unknown"}
              </Badge>
            </div>
            {project.projectCode && (
              <p className="text-gray-600 dark:text-gray-400 text-sm mt-1">{project.projectCode}</p>
            )}
            
            {/* Owner and Collaborators */}
            {(() => {
              const owners = getUsersByIds(project.owner);
              const collaborators = getUsersByIds(project.collaborators);
              const owner = owners.length > 0 ? owners[0] : null;
              
              if (!owner && collaborators.length === 0) return null;
              
              return (
                <div className="flex flex-wrap gap-4 mt-3">
                  {owner && (
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-muted-foreground">Owner:</span>
                      <div className="flex items-center gap-1.5">
                        <NotionAvatar 
                          name={owner.name}
                          profileImage={owner.profileImage}
                          className="h-6 w-6"
                        />
                        <span className="text-sm font-medium">{owner.name}</span>
                      </div>
                    </div>
                  )}
                  {collaborators.length > 0 && (
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-muted-foreground">Team:</span>
                      <div className="flex items-center gap-1">
                        {collaborators.map((collab) => (
                          <div key={collab.id} className="flex items-center gap-1.5">
                            <NotionAvatar 
                              name={collab.name}
                              profileImage={collab.profileImage}
                              className="h-6 w-6"
                            />
                            <span className="text-sm">{collab.name}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              );
            })()}
            
            {/* Task Contributors */}
            {(() => {
              // Collect all unique contributor IDs from sprint tasks
              const contributorIds = new Set<string>();
              sprintTasks.forEach(task => {
                if (Array.isArray(task.contributors)) {
                  task.contributors.forEach(id => contributorIds.add(id));
                }
              });
              
              if (contributorIds.size === 0) return null;
              
              // Match contributor IDs with resources
              const matchedResources: any[] = [];
              const unmatchedCount = Array.from(contributorIds).filter(id => {
                const resource = resources.find(r => r.id === id);
                if (resource) {
                  matchedResources.push(resource);
                  return false;
                }
                return true;
              }).length;
              
              if (matchedResources.length === 0 && unmatchedCount === 0) return null;
              
              return (
                <div className="flex items-center gap-2 mt-2">
                  <span className="text-sm text-muted-foreground">Contributors:</span>
                  <div className="flex items-center gap-2">
                    {matchedResources.length > 0 && (
                      <>
                        {matchedResources.map((resource) => (
                          <span key={resource.id} className="text-sm">{resource.name}</span>
                        )).reduce((prev, curr, idx) => {
                          return prev === null ? [curr] : [...prev, <span key={`sep-${idx}`} className="text-muted-foreground">,</span>, curr];
                        }, null as any)}
                      </>
                    )}
                    {unmatchedCount > 0 && (
                      <span className="text-sm text-muted-foreground">
                        {matchedResources.length > 0 && ', +'}
                        {matchedResources.length === 0 && unmatchedCount}
                        {matchedResources.length > 0 && unmatchedCount}
                      </span>
                    )}
                  </div>
                </div>
              );
            })()}
          </div>
          
          <div className="flex items-center gap-2">
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  onClick={() => window.open(`https://www.notion.so/${project.id.replace(/-/g, '')}`, '_blank')}
                  variant="outline"
                  size="icon"
                >
                  <NotionIcon size={16} />
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                <p>View in Notion</p>
              </TooltipContent>
            </Tooltip>
            <Button onClick={handleRefresh} disabled={refreshing}>
              <RefreshCw className={`h-4 w-4 mr-2 ${refreshing ? "animate-spin" : ""}`} />
              Refresh
            </Button>
          </div>
        </div>
      </div>

      {/* Project KPIs - Match Dashboard */}
      <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-5">
        {/* Capacity */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Capacity</CardTitle>
            <Users className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            {loadingSprints ? (
              <Spinner className="h-6 w-6" />
            ) : (
              <>
                <div className="text-2xl font-bold">{(projectKPIs.capacity / 8).toFixed(1)}d</div>
                <p className="text-xs text-muted-foreground mt-1">
                  Estimated hours ({projectKPIs.contributors} contributor{projectKPIs.contributors !== 1 ? 's' : ''})
                </p>
              </>
            )}
          </CardContent>
        </Card>

        {/* Utilization */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Utilization</CardTitle>
            <TrendingUp className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            {loadingSprints ? (
              <Spinner className="h-6 w-6" />
            ) : (
              <>
                <div className="text-2xl font-bold">{projectKPIs.utilization}%</div>
                <p className="text-xs text-muted-foreground mt-1">
                  Tasks in progress
                </p>
              </>
            )}
          </CardContent>
        </Card>

        {/* Budget Health */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Budget Health</CardTitle>
            <DollarSign className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <>
              <div className={`text-2xl font-bold ${
                projectKPIs.budgetHealth >= 70 ? 'text-green-600' : 
                projectKPIs.budgetHealth >= 40 ? 'text-yellow-600' : 
                'text-red-600'
              }`}>
                {projectKPIs.budgetHealth}%
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                Budget remaining
              </p>
            </>
          </CardContent>
        </Card>

        {/* Quality Score */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Quality Score</CardTitle>
            <Star className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <>
              <div className="text-2xl font-bold">{projectKPIs.qualityScore}%</div>
              <p className="text-xs text-muted-foreground mt-1">
                Project progress
              </p>
            </>
          </CardContent>
        </Card>

        {/* Timeline Risk */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Timeline Risk</CardTitle>
            <AlertTriangle className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            {loadingSprints ? (
              <Spinner className="h-6 w-6" />
            ) : (
              <>
                <div className={`text-2xl font-bold ${
                  projectKPIs.timelineRisk <= 20 ? 'text-green-600' : 
                  projectKPIs.timelineRisk <= 50 ? 'text-yellow-600' : 
                  'text-red-600'
                }`}>
                  {projectKPIs.timelineRisk}%
                </div>
                <p className="text-xs text-muted-foreground mt-1">
                  Tasks at risk
                </p>
              </>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Quick Stats */}
      <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Tasks</CardTitle>
            <CheckCircle className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{overallTaskStats.total}</div>
            <p className="text-xs text-muted-foreground">
              {completionRate}% completed
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">In Progress</CardTitle>
            <Clock className="h-4 w-4 text-blue-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{overallTaskStats.inProgress}</div>
            <p className="text-xs text-muted-foreground">
              Active tasks
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">To Do</CardTitle>
            <AlertCircle className="h-4 w-4 text-yellow-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{overallTaskStats.todo}</div>
            <p className="text-xs text-muted-foreground">
              Pending tasks
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Overdue</CardTitle>
            <AlertCircle className="h-4 w-4 text-red-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{overallTaskStats.overdue}</div>
            <p className="text-xs text-muted-foreground">
              Past deadline
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Main Content */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6 mt-8">
        <TabsList>
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="tasks">Tasks ({overallTaskStats.total})</TabsTrigger>
          <TabsTrigger value="sprints">Sprint Board ({sprintTasks.length})</TabsTrigger>
          <TabsTrigger value="budget">
            Budget {realizedBudgetData.totalHours > 0 && `(${realizedBudgetData.totalHours.toFixed(0)}h)`}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="space-y-4">
          <div className="grid gap-6 md:grid-cols-2">
            <Card>
              <CardHeader>
                <CardTitle>Project Details</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {project.description && (
                  <div>
                    <h4 className="text-sm font-medium mb-2">Description</h4>
                    <p className="text-sm text-muted-foreground">{project.description}</p>
                  </div>
                )}

                {project.priority && (
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-muted-foreground">Priority</span>
                    <span className={`text-sm font-medium ${getPriorityColor(project.priority)}`}>
                      {project.priority}
                    </span>
                  </div>
                )}

                {(project.startDate || project.endDate) && (
                  <div>
                    <h4 className="text-sm font-medium mb-2 flex items-center gap-2">
                      <Calendar className="h-4 w-4" />
                      Timeline
                    </h4>
                    <div className="space-y-1 text-sm">
                      {project.startDate && (
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Start:</span>
                          <span>{new Date(project.startDate).toLocaleDateString()}</span>
                        </div>
                      )}
                      {project.endDate && (
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">End:</span>
                          <span>{new Date(project.endDate).toLocaleDateString()}</span>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {project.team && project.team.length > 0 && (
                  <div>
                    <h4 className="text-sm font-medium mb-2 flex items-center gap-2">
                      <Users className="h-4 w-4" />
                      Team ({project.team.length})
                    </h4>
                    <div className="flex flex-wrap gap-2">
                      {project.team.map((member, idx) => (
                        <Badge key={idx} variant="outline">{member}</Badge>
                      ))}
                    </div>
                  </div>
                )}

                {project.gdriveRoom && (
                  <div>
                    <h4 className="text-sm font-medium mb-2 flex items-center gap-2">
                      <FolderOpen className="h-4 w-4" />
                      Resources
                    </h4>
                    <div className="space-y-1">
                      {project.gdriveRoom.split(' ').filter(url => url.trim()).map((url, idx) => (
                        <a
                          key={idx}
                          href={url.trim()}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-sm text-blue-500 hover:underline flex items-center gap-1"
                        >
                          <ExternalLink className="h-3 w-3" />
                          Google Drive Folder {idx + 1}
                        </a>
                      ))}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Budget & Progress */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center justify-between">
                  <span>Budget & Progress</span>
                  {realizedBudgetData.totalHours > 0 && (
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => setActiveTab("budget")}
                    >
                      View Details
                    </Button>
                  )}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Budget Section - Updated with realized from timesheets */}
                <div>
                  <h4 className="text-sm font-medium mb-3 flex items-center gap-2">
                    <DollarSign className="h-4 w-4" />
                    Budget
                  </h4>
                  <div className="space-y-3">
                    {/* Sold Budget */}
                    {project.budget ? (
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-muted-foreground">Budget Sold:</span>
                        <span className="text-lg font-bold">{formatBudget(project.budget)}</span>
                      </div>
                    ) : (
                      <div className="flex items-center gap-2 text-amber-600 bg-amber-50 dark:bg-amber-900/20 px-3 py-2 rounded-lg">
                        <AlertTriangle className="h-4 w-4" />
                        <span className="text-sm">No budget set for this project</span>
                      </div>
                    )}
                    
                    {/* Realized Budget from Timesheets */}
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-muted-foreground">
                        Realized Budget ({realizedBudgetData.totalHours.toFixed(1)}h tracked):
                      </span>
                      <span className={`text-lg font-bold ${
                        project.budget && realizedBudgetData.totalRealized > Number(project.budget) 
                          ? 'text-red-600' 
                          : ''
                      }`}>
                        ${realizedBudgetData.totalRealized.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                      </span>
                    </div>
                    
                    {/* Budget Health Progress Bar */}
                    {project.budget && Number(project.budget) > 0 && (
                      (() => {
                        const soldBudget = Number(project.budget)
                        const budgetUsedPercent = Math.round((realizedBudgetData.totalRealized / soldBudget) * 100)
                        const isOverBudget = budgetUsedPercent > 100
                        const isNearBudget = budgetUsedPercent >= 80
                        
                        return (
                          <>
                            <Progress 
                              value={Math.min(100, budgetUsedPercent)} 
                              className={`mt-2 ${
                                isOverBudget ? '[&>div]:bg-red-500' :
                                isNearBudget ? '[&>div]:bg-orange-500' :
                                '[&>div]:bg-green-500'
                              }`}
                            />
                            <div className="flex justify-between text-xs text-muted-foreground">
                              <span>
                                ${(soldBudget - realizedBudgetData.totalRealized).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })} remaining
                              </span>
                              <span className={`font-medium ${
                                isOverBudget ? 'text-red-600' :
                                isNearBudget ? 'text-orange-600' :
                                'text-green-600'
                              }`}>
                                {budgetUsedPercent}% used
                              </span>
                            </div>
                          </>
                        )
                      })()
                    )}
                  </div>
                </div>

                {project.progress !== undefined && (
                  <div>
                    <h4 className="text-sm font-medium mb-3">Overall Progress</h4>
                    <div className="space-y-2">
                      <div className="flex justify-between text-sm">
                        <span className="text-muted-foreground">Completion</span>
                        <span className="font-medium">{project.progress}%</span>
                      </div>
                      <Progress value={project.progress} />
                    </div>
                  </div>
                )}

                <div>
                  <h4 className="text-sm font-medium mb-3">Task Completion</h4>
                  <div className="space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Tasks Completed</span>
                      <span className="font-medium">{overallTaskStats.completed} / {overallTaskStats.total}</span>
                    </div>
                    <Progress value={completionRate} />
                    <p className="text-xs text-muted-foreground">
                      {overallTaskStats.completed} completed, {overallTaskStats.inProgress} in progress, {overallTaskStats.todo} to do
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="tasks" className="space-y-4">
          {sprintTasks.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center">
                <p className="text-muted-foreground">No tasks found for this project</p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-4">
              {/* Task Filters */}
              <div className="rounded-xl border bg-white/80 p-4 shadow-sm dark:bg-slate-900/40">
                <div className="flex flex-col gap-4">
                  <div className="flex flex-col gap-3 lg:flex-row lg:items-center">
                    <div className="relative flex-1">
                      <Search className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
                      <Input
                        value={taskSearch}
                        onChange={event => setTaskSearch(event.target.value)}
                        placeholder="Search tasks by title, tag, or owner"
                        className="pl-9"
                      />
                    </div>
                    <div className="flex flex-wrap items-center gap-2">
                      <div className="flex items-center gap-2 text-sm text-muted-foreground">
                        <Filter className="h-4 w-4" />
                        <span>Filters</span>
                      </div>
                      {hasActiveTaskFilters && (
                        <Button variant="ghost" size="sm" onClick={resetTaskFilters}>
                          Clear filters
                        </Button>
                      )}
                    </div>
                  </div>

                  {statusOptions.length > 0 && (
                    <div className="flex flex-col gap-2">
                      <span className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                        Status
                      </span>
                      <div className="flex flex-wrap gap-2">
                        {statusOptions.map(status => {
                          const isSelected = selectedStatuses.includes(status)
                          return (
                            <Button
                              key={status}
                              type="button"
                              size="sm"
                              variant={isSelected ? "default" : "outline"}
                              className={`rounded-full ${
                                isSelected ? "" : "bg-transparent"
                              }`}
                              aria-pressed={isSelected}
                              onClick={() => toggleStatus(status)}
                            >
                              {status}
                            </Button>
                          )
                        })}
                      </div>
                    </div>
                  )}

                  {availableTags.length > 0 && (
                    <div className="flex flex-col gap-2">
                      <span className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                        Tags
                      </span>
                      <div className="flex flex-wrap gap-2">
                        {availableTags.map(tag => {
                          const isSelected = selectedTags.includes(tag)
                          return (
                            <Button
                              key={tag}
                              type="button"
                              size="sm"
                              variant={isSelected ? "default" : "outline"}
                              className={`rounded-full ${
                                isSelected ? "" : "bg-transparent"
                              }`}
                              aria-pressed={isSelected}
                              onClick={() => toggleTag(tag)}
                            >
                              {tag}
                            </Button>
                          )
                        })}
                      </div>
                    </div>
                  )}

                  <div className="flex flex-wrap items-center justify-between gap-2 text-xs text-muted-foreground">
                    <span>
                      Showing {filteredTasks.length} of {sprintTasks.length} tasks
                    </span>
                    {!hasActiveTaskFilters && sprintTasks.length > 0 && (
                      <span>Use filters to focus the backlog</span>
                    )}
                  </div>
                </div>
              </div>

              {/* Task Status Breakdown */}
              <div className="grid gap-4 md:grid-cols-3">
                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-sm font-medium">To Do</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold">{filteredTaskStats.todo}</div>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-sm font-medium">In Progress</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold">{filteredTaskStats.inProgress}</div>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-sm font-medium">Completed</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold">{filteredTaskStats.completed}</div>
                  </CardContent>
                </Card>
              </div>

              {/* Task List */}
              <div className="space-y-3">
                {filteredTasks.length === 0 && (
                  <Card>
                    <CardContent className="py-10 text-center text-sm text-muted-foreground">
                      {hasActiveTaskFilters
                        ? "No tasks match the current filters"
                        : "No tasks available"}
                    </CardContent>
                  </Card>
                )}
                {filteredTasks.length > 0 && renderedTaskCards}
              </div>
            </div>
          )}
        </TabsContent>

        {/* Sprint Board Tab */}
        <TabsContent value="sprints" className="space-y-4">
          {sprints.length === 0 ? (
            <Card>
              <CardContent className="flex flex-col items-center justify-center py-12">
                <Calendar className="h-12 w-12 text-muted-foreground mb-4" />
                <p className="text-lg font-medium text-muted-foreground">No sprints available</p>
                <p className="text-sm text-muted-foreground">Sprint data will appear here once configured</p>
              </CardContent>
            </Card>
          ) : (
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle>Sprint Timeline</CardTitle>
                  <div className="flex items-center gap-2">
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => setCurrentSprintOffset(currentSprintOffset - 1)}
                      disabled={currentSprintOffset <= 0}
                    >
                      <ChevronLeft className="h-4 w-4" />
                      Previous
                    </Button>
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => {
                        const currentSprintIndex = sprints.findIndex(s => 
                          s.status?.toLowerCase() === 'current' || 
                          s.status?.toLowerCase() === 'active' || 
                          s.status?.toLowerCase() === 'in progress'
                        )
                        if (currentSprintIndex !== -1) {
                          setCurrentSprintOffset(Math.max(0, currentSprintIndex - 1))
                        } else {
                          setCurrentSprintOffset(0)
                        }
                      }}
                    >
                      Current
                    </Button>
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => setCurrentSprintOffset(currentSprintOffset + 1)}
                      disabled={currentSprintOffset >= sprints.length - 3}
                    >
                      Next
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="grid gap-4 md:grid-cols-3">
                  {sprints.slice(currentSprintOffset, currentSprintOffset + 3).map((sprint) => {
                    const isCurrentSprint = sprint.status?.toLowerCase() === 'active' || sprint.status?.toLowerCase() === 'in progress' || sprint.status?.toLowerCase() === 'current'
                    
                    // Calculate sprint name
                    const calculateSprintName = (startDate?: string, endDate?: string): string => {
                      if (!startDate || !endDate) return sprint.name || 'Sprint'
                      
                      const start = new Date(startDate)
                      const end = new Date(endDate)
                      const getWeekNumber = (date: Date): { week: number; year: number } => {
                        const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()))
                        const dayNum = d.getUTCDay() || 7
                        d.setUTCDate(d.getUTCDate() + 4 - dayNum)
                        const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1))
                        const weekNo = Math.ceil((((d.getTime() - yearStart.getTime()) / 86400000) + 1) / 7)
                        return { week: weekNo, year: d.getUTCFullYear() }
                      }
                      
                      const formatWeekLabel = (week: number, year: number): string => {
                        return `W${week.toString().padStart(2, '0')}.${year.toString().slice(-2)}`
                      }
                      
                      const { week: startWeek, year: startYear } = getWeekNumber(start)
                      const { week: endWeek, year: endYear } = getWeekNumber(end)
                      
                      if (startWeek === endWeek && startYear === endYear) {
                        return formatWeekLabel(startWeek, startYear)
                      }
                      
                      return `${formatWeekLabel(startWeek, startYear)} - ${formatWeekLabel(endWeek, endYear)}`
                    }
                    
                    const sprintName = calculateSprintName(sprint.startDate, sprint.endDate)
                    
                    // Get tasks for this sprint
                    const sprintTaskList = sprintTasks.filter(task => 
                      task.sprints.includes(sprint.id)
                    )
                    
                    // Sort tasks by status
                    const statusOrder = ['backlog', 'in progress', 'quality check', 'done', 'archived']
                    const sortedTaskList = [...sprintTaskList].sort((a, b) => {
                      const statusA = a.status?.toLowerCase() || ''
                      const statusB = b.status?.toLowerCase() || ''
                      const indexA = statusOrder.findIndex(s => statusA.includes(s))
                      const indexB = statusOrder.findIndex(s => statusB.includes(s))
                      const orderA = indexA === -1 ? 999 : indexA
                      const orderB = indexB === -1 ? 999 : indexB
                      return orderA - orderB
                    })
                    
                    // Calculate hours by tag
                    const hoursByTag: Record<string, { estimated: number, realised: number }> = {}
                    sprintTaskList.forEach(task => {
                      if (task.tags && task.tags.length > 0) {
                        task.tags.forEach(tag => {
                          if (!hoursByTag[tag]) {
                            hoursByTag[tag] = { estimated: 0, realised: 0 }
                          }
                          hoursByTag[tag].estimated += task.estimatedHours || 0
                          hoursByTag[tag].realised += task.realisedHours || 0
                        })
                      }
                    })
                    
                    const completedTasks = sprintTaskList.filter(task => 
                      task.status?.toLowerCase() === 'done' || task.status?.toLowerCase() === 'completed'
                    ).length
                    
                    const totalTasks = sprintTaskList.length
                    const progress = totalTasks > 0 ? Math.round((completedTasks / totalTasks) * 100) : 0
                    
                    return (
                      <div 
                        key={sprint.id}
                        className={`border rounded-lg p-4 ${isCurrentSprint ? 'border-primary bg-primary/5' : 'border-border'}`}
                      >
                        <div className="mb-3">
                          <div className="flex items-center justify-between mb-1">
                            <h3 className="font-semibold text-lg">{sprintName}</h3>
                            {isCurrentSprint && (
                              <Badge 
                                className={`text-xs ${getStatusColors('in-progress').bg} ${getStatusColors('in-progress').text} ${getStatusColors('in-progress').border} border`}
                              >
                                Active
                              </Badge>
                            )}
                            {sprint.status && !isCurrentSprint && (
                              <Badge 
                                className={`text-xs ${getStatusColors(sprint.status).bg} ${getStatusColors(sprint.status).text} ${getStatusColors(sprint.status).border} border`}
                              >
                                {sprint.status}
                              </Badge>
                            )}
                          </div>
                          {(sprint.startDate || sprint.endDate) && (
                            <>
                              <p className="text-xs text-muted-foreground">
                                {sprint.startDate && new Date(sprint.startDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                                {sprint.startDate && sprint.endDate && ' - '}
                                {sprint.endDate && new Date(sprint.endDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                              </p>
                              {Object.keys(hoursByTag).length > 0 && (
                                <div className="mt-2 space-y-1">
                                  {Object.entries(hoursByTag).map(([tag, hours]) => {
                                    const tagColors = getTagColors(tag);
                                    const estimatedDays = (hours.estimated / 8).toFixed(1)
                                    const realisedDays = (hours.realised / 8).toFixed(1)
                                    return (
                                      <div key={tag} className="text-xs flex items-center gap-2">
                                        <Badge 
                                          className={`text-xs px-1.5 py-0 ${tagColors.bg} ${tagColors.text} ${tagColors.border} border`}
                                        >
                                          {tag}
                                        </Badge>
                                        <span className="text-muted-foreground">
                                          {hours.realised.toFixed(1)}h / {hours.estimated.toFixed(1)}h ({realisedDays}d / {estimatedDays}d)
                                        </span>
                                      </div>
                                    )
                                  })}
                                </div>
                              )}
                            </>
                          )}
                          {sprint.goal && (
                            <p className="text-xs text-muted-foreground mt-2 italic">{sprint.goal}</p>
                          )}
                        </div>
                        
                        {/* Sprint Progress */}
                        {totalTasks > 0 && (
                          <div className="space-y-2 mb-3">
                            <div className="flex justify-between text-sm">
                              <span className="text-muted-foreground">Progress</span>
                              <span className="font-medium">{completedTasks}/{totalTasks} tasks</span>
                            </div>
                            <Progress value={progress} />
                          </div>
                        )}
                        
                        {/* Sprint Tasks */}
                        <div className="space-y-2">
                          {sprintTaskList.length === 0 ? (
                            <p className="text-sm text-muted-foreground italic">No tasks in this sprint</p>
                          ) : (
                            <div className="space-y-2 max-h-96 overflow-y-auto">
                              {sortedTaskList.map(task => {
                                const isOverdue = task.due && task.due < new Date() && 
                                  task.status?.toLowerCase() !== 'done' && 
                                  task.status?.toLowerCase() !== 'completed'
                                
                                return (
                                  <div 
                                    key={task.id}
                                    onClick={() => navigate(`/production-management/task/${task.id}`, { state: { task } })}
                                    className={`p-3 rounded-lg bg-card border-l-4 border border-border hover:border-primary/50 transition-colors shadow-sm cursor-pointer ${
                                      isOverdue ? 'border-l-red-500' : ''
                                    }`}
                                    style={{ borderLeftColor: isOverdue ? '#ef4444' : getStatusColors(task.status).borderColorValue }}
                                  >
                                    <div className="flex items-start justify-between gap-2 mb-2">
                                      <p className="text-sm font-medium line-clamp-2 flex-1">
                                        {task.title}
                                      </p>
                                      <Badge 
                                        className={`text-xs shrink-0 ${getStatusColors(task.status).bg} ${getStatusColors(task.status).text} ${getStatusColors(task.status).border} border`}
                                      >
                                        {task.status || "Not Started"}
                                      </Badge>
                                    </div>
                                    
                                    {task.description && (
                                      <p className="text-xs text-muted-foreground line-clamp-2 mb-2">
                                        {task.description}
                                      </p>
                                    )}
                                    
                                    <div className="flex flex-wrap gap-3 text-xs text-muted-foreground">
                                      {task.due && (
                                        <div className={`flex items-center gap-1 ${isOverdue ? "text-red-500 font-medium" : ""}`}>
                                          <Calendar className="h-3 w-3" />
                                          {task.due.toLocaleDateString()}
                                          {isOverdue && " (!)"}
                                        </div>
                                      )}
                                      {task.priority && (
                                        <div className="flex items-center gap-1">
                                          <AlertCircle className="h-3 w-3" />
                                          {task.priority}
                                        </div>
                                      )}
                                      {(task.estimatedHours !== undefined || task.realisedHours !== undefined) && (
                                        <div className="flex items-center gap-2">
                                          <Clock className="h-3 w-3" />
                                          {task.realisedHours !== undefined && task.estimatedHours !== undefined ? (
                                            <>
                                              <span className={`${
                                                task.realisedHours > task.estimatedHours ? 'text-red-600 font-semibold' :
                                                task.realisedHours >= task.estimatedHours * 0.8 ? 'text-orange-600 font-medium' :
                                                'text-green-600'
                                              }`}>
                                                {task.realisedHours.toFixed(1)}h / {task.estimatedHours}h
                                              </span>
                                              <div className="flex-1 min-w-[60px] h-2 bg-gray-200 rounded-full overflow-hidden">
                                                <div 
                                                  className={`h-full transition-all ${
                                                    task.realisedHours > task.estimatedHours ? 'bg-red-500' :
                                                    task.realisedHours >= task.estimatedHours * 0.8 ? 'bg-orange-500' :
                                                    'bg-green-500'
                                                  }`}
                                                  style={{ width: `${Math.min(100, (task.realisedHours / task.estimatedHours) * 100)}%` }}
                                                />
                                              </div>
                                            </>
                                          ) : task.realisedHours !== undefined ? (
                                            <span>{task.realisedHours.toFixed(1)}h</span>
                                          ) : (
                                            <span>{task.estimatedHours}h</span>
                                          )}
                                        </div>
                                      )}
                                      {task.contributors && task.contributors.length > 0 && (
                                        <div className="flex items-center gap-1">
                                          <Users className="h-3 w-3" />
                                          {getContributorNames(task.contributors) || task.contributors.length}
                                        </div>
                                      )}
                                      {task.url && (
                                        <a
                                          href={task.url}
                                          target="_blank"
                                          rel="noopener noreferrer"
                                          className="flex items-center gap-1 text-blue-500 hover:underline"
                                          onClick={(e) => e.stopPropagation()}
                                        >
                                          <ExternalLink className="h-3 w-3" />
                                          Notion
                                        </a>
                                      )}
                                    </div>
                                    
                                    {task.tags && task.tags.length > 0 && (
                                      <div className="flex flex-wrap gap-1 mt-2">
                                        {task.tags.map((tag, idx) => (
                                          <Badge key={idx} variant="outline" className="text-xs">
                                            {tag}
                                          </Badge>
                                        ))}
                                      </div>
                                    )}
                                  </div>
                                )
                              })}
                            </div>
                          )}
                        </div>
                      </div>
                    )
                  })}
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Budget Tab */}
        <TabsContent value="budget" className="space-y-6">
          {/* Budget Summary Cards */}
          <div className="grid gap-4 md:grid-cols-4">
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground">Budget Sold</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">
                  {project.budget ? `$${Number(project.budget).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}` : '-'}
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground">Realized Budget</CardTitle>
              </CardHeader>
              <CardContent>
                <div className={`text-2xl font-bold ${
                  project.budget && realizedBudgetData.totalRealized > Number(project.budget) ? 'text-red-600' : ''
                }`}>
                  ${realizedBudgetData.totalRealized.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground">Total Hours</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{realizedBudgetData.totalHours.toFixed(1)}h</div>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground">Budget Health</CardTitle>
              </CardHeader>
              <CardContent>
                {project.budget && Number(project.budget) > 0 ? (
                  (() => {
                    const percent = Math.round((realizedBudgetData.totalRealized / Number(project.budget)) * 100)
                    return (
                      <div className={`text-2xl font-bold ${
                        percent > 100 ? 'text-red-600' :
                        percent >= 80 ? 'text-orange-600' :
                        'text-green-600'
                      }`}>
                        {percent}%
                      </div>
                    )
                  })()
                ) : (
                  <div className="text-2xl font-bold text-amber-600">N/A</div>
                )}
              </CardContent>
            </Card>
          </div>

          {realizedBudgetData.totalHours === 0 ? (
            <Card>
              <CardContent className="py-12 text-center">
                <DollarSign className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                <p className="text-muted-foreground">No timesheet data available for this project</p>
                <p className="text-sm text-muted-foreground mt-2">Timesheets linked to tasks in this project will appear here</p>
              </CardContent>
            </Card>
          ) : (
            <>
              {/* Charts Row */}
              <div className="grid gap-6 md:grid-cols-2">
                {/* Cost by Task Chart */}
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">Cost by Task</CardTitle>
                  </CardHeader>
                  <CardContent>
                    {realizedBudgetData.byTask.length > 0 ? (
                      <ChartContainer
                        config={{
                          cost: { label: 'Cost', color: '#3b82f6' }
                        }}
                        className="h-[300px]"
                      >
                        <BarChart 
                          data={realizedBudgetData.byTask
                            .sort((a, b) => b.cost - a.cost)
                            .slice(0, 10)
                            .map(t => ({
                              name: t.taskTitle.length > 25 ? t.taskTitle.substring(0, 25) + '...' : t.taskTitle,
                              cost: Math.round(t.cost),
                              hours: t.hours
                            }))}
                          layout="vertical"
                          margin={{ top: 5, right: 30, left: 100, bottom: 5 }}
                        >
                          <XAxis type="number" tickFormatter={(val: number) => `$${val}`} />
                          <YAxis type="category" dataKey="name" width={100} tick={{ fontSize: 12 }} />
                          <ChartTooltip 
                            content={<ChartTooltipContent />}
                            formatter={(value: number) => [`$${value.toLocaleString('en-US')}`, 'Cost']}
                          />
                          <Bar dataKey="cost" fill="#3b82f6" radius={[0, 4, 4, 0]} />
                        </BarChart>
                      </ChartContainer>
                    ) : (
                      <p className="text-sm text-muted-foreground text-center py-8">No task data available</p>
                    )}
                  </CardContent>
                </Card>

                {/* Cost by Resource Chart */}
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">Cost by Resource</CardTitle>
                  </CardHeader>
                  <CardContent>
                    {realizedBudgetData.byResource.length > 0 ? (
                      <ChartContainer
                        config={realizedBudgetData.byResource.reduce((acc, r) => ({
                          ...acc,
                          [r.resourceName]: { label: r.resourceName, color: '#3b82f6' }
                        }), {})}
                        className="h-[300px]"
                      >
                        <PieChart>
                          <Pie
                            data={realizedBudgetData.byResource.map((r, i) => ({
                              name: r.resourceName,
                              value: Math.round(r.cost),
                              hours: r.hours,
                              fill: ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899', '#14b8a6', '#f97316'][i % 8]
                            }))}
                            dataKey="value"
                            nameKey="name"
                            cx="50%"
                            cy="50%"
                            outerRadius={100}
                            label={({ name, percent }: { name: string; percent: number }) => `${name} (${(percent * 100).toFixed(0)}%)`}
                            labelLine={true}
                          >
                            {realizedBudgetData.byResource.map((_, index) => (
                              <Cell 
                                key={`cell-${index}`} 
                                fill={['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899', '#14b8a6', '#f97316'][index % 8]} 
                              />
                            ))}
                          </Pie>
                          <ChartTooltip 
                            content={<ChartTooltipContent />}
                            formatter={(value: number) => [`$${value.toLocaleString('en-US')}`, 'Cost']}
                          />
                        </PieChart>
                      </ChartContainer>
                    ) : (
                      <p className="text-sm text-muted-foreground text-center py-8">No resource data available</p>
                    )}
                  </CardContent>
                </Card>
              </div>

              {/* Detailed Tables */}
              <div className="grid gap-6 md:grid-cols-2">
                {/* Cost by Task Table */}
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">Breakdown by Task</CardTitle>
                    <CardDescription>{realizedBudgetData.byTask.length} tasks with tracked time</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Task</TableHead>
                          <TableHead className="text-right">Hours</TableHead>
                          <TableHead className="text-right">Cost</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {realizedBudgetData.byTask
                          .sort((a, b) => b.cost - a.cost)
                          .map(task => (
                            <TableRow key={task.taskId}>
                              <TableCell className="font-medium max-w-[200px] truncate" title={task.taskTitle}>
                                {task.taskTitle}
                              </TableCell>
                              <TableCell className="text-right">{task.hours.toFixed(1)}h</TableCell>
                              <TableCell className="text-right font-medium">
                                ${task.cost.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                              </TableCell>
                            </TableRow>
                          ))}
                        <TableRow className="font-bold bg-muted/50">
                          <TableCell>Total</TableCell>
                          <TableCell className="text-right">{realizedBudgetData.totalHours.toFixed(1)}h</TableCell>
                          <TableCell className="text-right">
                            ${realizedBudgetData.totalRealized.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </CardContent>
                </Card>

                {/* Cost by Resource Table */}
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">Breakdown by Resource</CardTitle>
                    <CardDescription>{realizedBudgetData.byResource.length} resources with tracked time</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Resource</TableHead>
                          <TableHead className="text-right">Hours</TableHead>
                          <TableHead className="text-right">Rate</TableHead>
                          <TableHead className="text-right">Cost</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {realizedBudgetData.byResource
                          .sort((a, b) => b.cost - a.cost)
                          .map(resource => (
                            <TableRow key={resource.resourceId}>
                              <TableCell className="font-medium">{resource.resourceName}</TableCell>
                              <TableCell className="text-right">{resource.hours.toFixed(1)}h</TableCell>
                              <TableCell className="text-right text-muted-foreground">
                                ${resource.hourlyRate}/h
                              </TableCell>
                              <TableCell className="text-right font-medium">
                                ${resource.cost.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                              </TableCell>
                            </TableRow>
                          ))}
                        <TableRow className="font-bold bg-muted/50">
                          <TableCell>Total</TableCell>
                          <TableCell className="text-right">{realizedBudgetData.totalHours.toFixed(1)}h</TableCell>
                          <TableCell className="text-right">-</TableCell>
                          <TableCell className="text-right">
                            ${realizedBudgetData.totalRealized.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </CardContent>
                </Card>
              </div>
            </>
          )}
        </TabsContent>
      </Tabs>
      </div>
    </div>
  )
}
